import API from '../api'
import { JobFinancialsData } from '../Items/JobCosting/jobCostingModel'
import { DashboardFilters, dashboardFiltersQuery } from './dashboardModel'

type SoldJobData = {
  jobId: number
  jobNumber: string
  name: string
  url: string
  revenue: number
  customerName: string
}

export type SoldJobRevenue = {
  soldJobRevenue: number
  jobs: SoldJobData[]
}

export type GraphingData = {
  datasets: [number[]],
  labels: string[]
}

export type EmployeeUtilization = {
  crewMemberId: number
  name: string
  email: string | null
  totalHours: number
  billableHours: number
  unbillableHours: number
  unbillablePercent: number | null
}

export type EmployeeUtilizationResult = {
  employees: EmployeeUtilization[]
}

export const SoldJobRevenueEndpoint = '/web/dashboard/sales-progress'
export const EmployeeUtilizationEndpoint = '/web/dashboard-reports/employee-utilization'
export const SalesLeadsEndpoint = '/graphing/cache/sales-leads'
export const WonLostEndpoint = '/graphing/cache/won-lost'

export const getSoldJobRevenue = async (start:string, end:string): Promise<SoldJobRevenue> => {
  return (await API.get(`${SoldJobRevenueEndpoint}?start=${start}&end=${end}`)).data
}

export const getSalesLeads = async (start:string, end:string): Promise<GraphingData> => {
  return (await API.get(`${SalesLeadsEndpoint}?starting=${start}&ending=${end}`)).data
}

export const getWonLost = async (start:string, end:string): Promise<GraphingData> => {
  return (await API.get(`${WonLostEndpoint}?starting=${start}&ending=${end}`)).data
}

// dashboard-reporting: the filtered reports use their own endpoints, so the ones above stay as they are.
export const FilteredSoldJobRevenueEndpoint = '/web/dashboard-reports/sales-progress'
export const FilteredWonLostEndpoint = '/web/dashboard-reports/won-lost'
export const FinancialsDataEndpoint = 'graphing/financials-data'

type WonLostCounts = {
  won: number
  lost: number
  pending: number
}

const withFilters = (url: string, filters: DashboardFilters): string => {
  const query = dashboardFiltersQuery(filters).toString()
  return query ? `${url}&${query}` : url
}

export const getFilteredSoldJobRevenue = async (start: string, end: string, filters: DashboardFilters): Promise<SoldJobRevenue> => {
  return (await API.get(withFilters(`${FilteredSoldJobRevenueEndpoint}?start=${start}&end=${end}`, filters))).data
}

// Shaped like the cached won-lost graph data, so the chart renders either one.
export const getFilteredWonLost = async (start: string, end: string, filters: DashboardFilters): Promise<GraphingData> => {
  const counts: WonLostCounts = (await API.get(withFilters(`${FilteredWonLostEndpoint}?starting=${start}&ending=${end}`, filters))).data
  return { datasets: [[counts.won, counts.lost, counts.pending]], labels: ['Won', 'Lost', 'Pending'] }
}

export const getFinancialsData = async (start: string, end: string, filters: DashboardFilters): Promise<JobFinancialsData> => {
  return (await API.post(FinancialsDataEndpoint, { start, end, ...filters })).data
}

// Takes the filters already narrowed by utilizationFilters; the endpoint includes the whole end day itself.
export const getEmployeeUtilization = async (start: string, end: string, filters: DashboardFilters): Promise<EmployeeUtilizationResult> => {
  return (await API.get(withFilters(`${EmployeeUtilizationEndpoint}?start=${start}&end=${end}`, filters))).data
}
